import { Link } from "@tanstack/react-router"
import { lowerCase } from "lodash-es"
import { type ReactNode, useEffect, useState } from "react"

import { PortfolioTags } from "@/admin/companies/portfolio-tags"
import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import { loadCompanyRunHistory } from "@/admin/monitoring/company-search-data"
import {
  companyActivity,
  type CompanyRunPhase,
  companyRunPhase,
  elapsedTime,
  formatRunStart,
  pluralize,
  statusToneByPhase,
} from "@/admin/monitoring/run-presentation"
import { StoredValue } from "@/admin/monitoring/stored-value"
import { UsageView } from "@/admin/monitoring/usage-view"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  type CompanyRunOutcome,
  dataByCompanyRunOutcome,
} from "@/constants/terminal/company-run-outcome"
import type {
  UnknownRecordWordCount,
  UnreadSourceRecordCount,
} from "@/db/schema/terminal-schema"
import { errorMessage } from "@/lib/error-message"
import { sourcePlatformDisplayName } from "@/terminal/research/platform-display-names"

// The Company Run's identity, status, run details and its place among the
// Company's other runs.
export const CompanyRunHeader = ({
  research,
  onSelectRun,
}: {
  research: CompanyRunResearch
  onSelectRun: (id: string) => void
}) => {
  const [history, setHistory] = useState<
    Awaited<ReturnType<typeof loadCompanyRunHistory>>
  >([])
  const [historyLimit, setHistoryLimit] = useState(50)
  const [historyError, setHistoryError] = useState<string | null>(null)
  useEffect(() => {
    let isCancelled = false
    void loadCompanyRunHistory({
      data: { companyRunId: research.companyRunId, limit: historyLimit },
    })
      .then((runs) => {
        if (!isCancelled) {
          setHistory(runs)
          setHistoryError(null)
        }
      })
      .catch((cause: unknown) => {
        if (!isCancelled) setHistoryError(errorMessage(cause, "History failed"))
      })
    return () => {
      isCancelled = true
    }
  }, [research.companyRunId, historyLimit])
  const phase = companyRunPhase(research)
  const currentRun = {
    companyRunId: research.companyRunId,
    startedAt: research.monitoringRunStartedAt,
    outcome: research.outcome,
  }
  // History loads after the page, so the current run leads until it arrives.
  const runs = history.some((run) => run.companyRunId === research.companyRunId)
    ? history
    : [currentRun, ...history]
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Link
          className="text-muted-foreground hover:text-foreground text-xs"
          to="/terminal/admin/runs"
          search={{ monitoringRunId: research.monitoringRunId }}
        >
          Monitoring run of {formatRunStart(research.monitoringRunStartedAt)}
        </Link>
        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          <Select value={research.companyRunId} onValueChange={onSelectRun}>
            <SelectTrigger
              aria-label="Runs for this company"
              className="h-8 w-auto min-w-0 gap-3 rounded-sm text-xs"
            >
              <SelectValue>
                <RunLabel {...currentRun} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {runs.map((run) => (
                <SelectItem
                  key={run.companyRunId}
                  value={run.companyRunId}
                  className="text-xs"
                >
                  <RunLabel startedAt={run.startedAt} outcome={run.outcome} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {history.length >= historyLimit && historyLimit < 1000 && (
            <button
              className={terminalControlVariants({ size: "m" })}
              onClick={() => setHistoryLimit(historyLimit + 50)}
            >
              Load older runs
            </button>
          )}
        </div>
      </div>
      {historyError && (
        <p role="alert" className="text-assessment-critical text-xs">
          Run history failed to load: {historyError}
        </p>
      )}
      <header className="space-y-3">
        <h1 className="font-display text-3xl leading-tight font-black">
          {research.displayName}
        </h1>
        <PortfolioTags company={research} />
        <p className="text-sm">
          <span className={statusToneByPhase[phase]}>
            {companyActivity(research)}.
          </span>{" "}
          <span className="text-muted-foreground">
            {runDuration(research, phase)}
          </span>
        </p>
        <RunDetails research={research} />
        {research.exceptionReason && (
          <div className="border-assessment-critical space-y-1 rounded-sm border p-3 text-sm">
            <p className="text-assessment-critical font-semibold">Exception</p>
            <StoredValue value={research.exceptionReason} />
          </div>
        )}
        {research.deferReason && (
          <div className="border-assessment-stressed space-y-1 rounded-sm border p-3 text-sm">
            <p className="text-assessment-stressed font-semibold">Deferred</p>
            <StoredValue value={research.deferReason} />
          </div>
        )}
        {research.executionSummary?.stop_reason && (
          <p className="text-muted-foreground text-sm">
            {research.executionSummary.stop_reason === "task_budget"
              ? "The Task budget held some Tasks for a later Run."
              : `Research stopped early: ${lowerCase(research.executionSummary.stop_reason)}.`}
          </p>
        )}
        <UnreadRecords
          groups={research.executionSummary?.unread_source_records ?? []}
        />
        <UnknownRecordWords
          groups={research.executionSummary?.unknown_record_words ?? []}
        />
      </header>
    </>
  )
}

// "3 contractor license records from {authority}", for both lists below.
const recordsFrom = (
  recordCount: number,
  recordKind: string,
  sourceKey: string
) =>
  `${pluralize(recordCount, `${lowerCase(recordKind)} record`)} from ${sourcePlatformDisplayName(sourceKey)}`

// The in-scope records the Assessment found no Detection Selection Rule
// reads: captured evidence that cannot become a Detection until a reader
// covers its kind and authority.
const UnreadRecords = ({ groups }: { groups: UnreadSourceRecordCount[] }) =>
  groups.length > 0 && (
    <ReadAuditList title="In-scope records no Detection rule reads">
      {groups.map(({ record_kind, source_key, record_count }) => (
        <li key={`${record_kind}:${source_key}`}>
          {recordsFrom(record_count, record_kind, source_key)}
        </li>
      ))}
    </ReadAuditList>
  )

// The words in in-scope records that the rule reading them does not know: a
// word that cannot select or resolve a Detection until a reader knows it
// (ADR-0194, an unknown authority word never selects and never resolves).
const UnknownRecordWords = ({ groups }: { groups: UnknownRecordWordCount[] }) =>
  groups.length > 0 && (
    <ReadAuditList title="Record words no Detection rule knows">
      {groups.map(({ record_kind, source_key, field, word, record_count }) => (
        <li key={`${record_kind}:${source_key}:${field}:${word}`}>
          {recordsFrom(record_count, record_kind, source_key)}:{" "}
          {lowerCase(field)} “{word}”
        </li>
      ))}
    </ReadAuditList>
  )

const ReadAuditList = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <div className="border-assessment-stressed space-y-1 rounded-sm border p-3 text-sm">
    <p className="text-assessment-stressed font-semibold">{title}</p>
    <ul className="space-y-0.5">{children}</ul>
  </div>
)

// Reference facts for the Company Run, beside the status they qualify.
const RunDetails = ({ research }: { research: CompanyRunResearch }) => {
  const [wasCopied, setWasCopied] = useState(false)
  return (
    <dl className="text-muted-foreground [&_dd]:text-foreground flex flex-wrap gap-x-6 gap-y-1 text-xs [&>div]:flex [&>div]:gap-2">
      <div>
        <dt>Admitted</dt>
        <dd>
          {research.admittedAt
            ? formatRunStart(research.admittedAt)
            : "Not admitted"}
        </dd>
      </div>
      {research.finishedAt && (
        <div>
          <dt>Finished</dt>
          <dd>{formatRunStart(research.finishedAt)}</dd>
        </div>
      )}
      <div>
        <dt>Policy versions</dt>
        <dd>{research.policyVersions}</dd>
      </div>
      <div>
        <dt>Company run</dt>
        <dd>
          <button
            title={`Copy ${research.companyRunId}`}
            className="underline-offset-2 hover:underline"
            onClick={() =>
              void navigator.clipboard
                .writeText(research.companyRunId)
                .then(() => {
                  setWasCopied(true)
                  window.setTimeout(() => setWasCopied(false), 2000)
                })
            }
          >
            {wasCopied ? "Copied" : research.companyRunId.slice(0, 8)}
          </button>
        </dd>
      </div>
    </dl>
  )
}

// What the Company Run spent, below the research it paid for.
export const CompanyRunSpend = ({
  research,
}: {
  research: CompanyRunResearch
}) => (
  <section className="max-w-xl space-y-3 border-t pt-8">
    <h2 className="text-sm font-semibold">Spend</h2>
    <UsageView usage={research.usage} />
  </section>
)

const RunLabel = ({
  startedAt,
  outcome,
}: {
  startedAt: string
  outcome: CompanyRunOutcome | null
}) => (
  <>
    {formatRunStart(startedAt)}{" "}
    <span className="text-muted-foreground">
      {outcome ? dataByCompanyRunOutcome[outcome].label : "In progress"}
    </span>
  </>
)

const runDuration = (
  { startedAt, finishedAt, monitoringRunStartedAt }: CompanyRunResearch,
  phase: CompanyRunPhase
) => {
  if (finishedAt) return `Took ${elapsedTime(startedAt, finishedAt)}.`
  if (phase === "active") return `Running for ${elapsedTime(startedAt, null)}.`
  return `Monitoring run started ${formatRunStart(monitoringRunStartedAt)}.`
}
